import './ApiKeyForm.scss';

import { Badge, Button, Col, Modal, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { regular, solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { Alert, Flex, Form, FormProps, Pagination, Select, Tooltip } from "antd";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { ControlPanelManager } from "../../api/ControlPanelManager";
import { useAuth } from "../../contexts/auth";
import { Role } from "../../models/User";
import { useToasts } from "../../contexts/toasts";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { sectionLabelsSelector } from "../../slices/sectionsSlice";
import { PagedResults } from "../../models/generics";
import { Applications } from "../../api/Applications";
import { Models } from "../../api/Models";
import { Llm } from "../../api/Llm";
import { BdaApplication } from "../../models/BdaApplication";
import { BdaModel } from "../../models/BdaModel";
import classNames from "classnames";
import { formatDate } from "../../utils";
import { Link } from "react-router-dom";
import { Rag } from '../../models/LLM';
import IconButton from '../common/IconButton';
import AppSearch from '../common/AppSearch';
import { debounce } from 'lodash';
import AppLoader from '../common/AppLoader';

type Props = {
  onSubmit?: (apiKey: any) => void;
  onCancel?: () => void;
}

const formProps: FormProps = {
  labelCol: {
    md: 6,
    lg: 4,
  },
  wrapperCol: {
    md: 18,
    lg: 20,
  }
};

const getItemTypeFromClient = (client: string) => {
  switch (client) {
    case 'bda': return 'application';
    case 'model': return 'model';
    case 'rag': return 'rag';
    default: return client;
  }
}
// TODO fix browsing/pagination
const getCurrentPageIds = (array: string[], currentPage: number, size: number) => {
  const startIndex = (currentPage - 1) * size;
  const endIndex = startIndex + size;
  return array.slice(startIndex, endIndex);
}

export default function ApiKeyForm({ onSubmit, onCancel }: Props) {
  const { loggedUser } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(8);
  const [totalItems, setTotalItems] = useState<number>();
  const [roles, setRoles] = useState<Role[] | undefined>([]);
  const [selectedItem, setSelectedItem] = useState<any>();
  const [labeledItems, setLabeledItems] = useState<Array<BdaApplication | BdaModel | Rag | any>>();
  const [searchText, setSearchText] = useState('');
  const [form] = Form.useForm();
  const selectedClient = Form.useWatch('client', form);
  const { showToast } = useToasts();

  const applicationLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "applications"));
  const ragLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "rags"));
  const modelLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "models"));
  const apiKeyLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "api-keys"));
  const apiKeyClientsOptions = useMemo(() => (window.__env.ALLOWED_API_KEYS_CLIENTS || []).map(client => ({ label: client, value: client })), []);

  const getRoleLabel = useCallback((client: string) => {
    switch (client) {
      case "bda": return applicationLabels.singular;
      case "model": return modelLabels.singular;
      case "rag": return ragLabels.singular;
      default: return "Role";
    }
  }, [applicationLabels, modelLabels, ragLabels]);

  const getPromiseFromSelectedClient = useCallback((params: URLSearchParams) => {
    switch (selectedClient) {
      case "model": return Models.getModels(params);
      case "rag": return Llm.getRags(params);
      case "bda": return Applications.getApplications(params);
      default: return Promise.resolve({ data: roles?.map(role => role.name) });
    }
  }, [selectedClient, roles]);

  const getItems = useCallback(() => {
    if (roles?.length && selectedClient) {
      const params = new URLSearchParams([['size', `${itemsPerPage}`]]);
      const pageIds = getCurrentPageIds(roles!.map((role) => role.name), currentPage, itemsPerPage);
      params.append("ids", pageIds.join(","));
      if (searchText) params.append("freeText", searchText);

      const promise = getPromiseFromSelectedClient(params);
      promise.then(
        ({ data }) => {
          setTotalItems(searchText ? (data as PagedResults<BdaApplication>).pageMetadata?.totalElements : roles.length);
          if (["bda", "model", "rag"].includes(selectedClient)) setLabeledItems((data as PagedResults<BdaApplication>).content);
          else setLabeledItems((data as string[]).map(role => ({ label: role, value: role })));
        },
        (err) => {
          setLabeledItems([]);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('user-role-labels-error', message, `User Role Labels Error ${status ? `(${status})` : ''}`, 'danger');
        },
      );
    }
    // eslint-disable-next-line
  }, [roles, selectedClient, currentPage, searchText]);

  const reset = useCallback(() => {
    setLabeledItems(undefined);
    getItems();
  }, [getItems]);

  // eslint-disable-next-line
  useEffect(reset, [roles, currentPage, itemsPerPage, searchText, showToast]);

  const getUserClientRoles = useCallback(() => {
    ControlPanelManager.getUserClientRoles(loggedUser!.sub as string, selectedClient)
      .then(
        ({ data }) => setRoles(data),
        (err) => {
          setRoles([]);
          setLabeledItems([]);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast("user-roles-error", message, `User Roles Error ${status ? `(${status})` : ""}`, "danger");
        },
      );
  }, [loggedUser, selectedClient, showToast]);

  useEffect(() => {
    if (selectedClient) {
      setRoles(undefined);
      setCurrentPage(1);
      setTotalItems(undefined);
      setLabeledItems(undefined);
      getUserClientRoles();
    }
  }, [selectedClient, getUserClientRoles]);

  const handlePageChange = useCallback((page: number, pageSize: number) => {
    setCurrentPage(page);
    setItemsPerPage(pageSize);
  }, []);

  const handleItemSelect = useCallback((value: string) => () => setSelectedItem(value), []);
  const handleSearchChange = useCallback((value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  }, []);

  const debounceSearchChange = useMemo(() => debounce(handleSearchChange, 500), [handleSearchChange]);

  const handleSubmit = useCallback((values: any) => {
    if (!selectedItem && !values.role) {
      showToast('missing-client-error', `You need to select one ${getRoleLabel(selectedClient)} before proceeding`, null, 'warning');
      return;
    }

    onSubmit?.({ ...values, role: values.role || `${selectedItem}` });
  }, [selectedItem, onSubmit, showToast, selectedClient, getRoleLabel]);

  const handleModuleChange = useCallback(() => {
    setSelectedItem(undefined);
    form.setFieldValue("role", undefined);
  }, [form]);

  return (
    <Form className="compact-form" {...formProps} form={form} onFinish={handleSubmit}>
      <Modal.Body>
        <Form.Item name="client" label="Module" rules={[{ required: true, message: `${apiKeyLabels.singular} Module is required` }]}>
          <Select placeholder="Select Module" allowClear options={apiKeyClientsOptions} onChange={handleModuleChange} />
        </Form.Item>

        <Form.Item noStyle dependencies={['client']}>
          {({ getFieldValue }) => {
            const client = getFieldValue("client");

            if (["bda", "model", "rag"].includes(client)) {
              return (
                <>
                  <Row className="mb-2 gx-2 align-items-center">
                    <Col>
                      <AppSearch placeholder={`Search ${getRoleLabel(client)}...`} onChange={debounceSearchChange} />
                    </Col>
                    <Col md="5">
                      <Pagination
                        pageSize={itemsPerPage}
                        current={currentPage}
                        total={totalItems}
                        onChange={handlePageChange}
                        showSizeChanger={false}
                        size="small"
                        simple
                      />
                    </Col>
                  </Row>

                  {!labeledItems && <AppLoader className="py-3" justify="center" size={80} />}

                  {labeledItems?.length === 0
                    ? <Alert type="info" showIcon message={`No ${getRoleLabel(client)} found.`} />
                    : labeledItems?.map(item => (
                      <SelectableItem
                        key={item.id}
                        item={item}
                        type={getItemTypeFromClient(selectedClient)}
                        selected={selectedItem === item.id}
                        onSelect={handleItemSelect(item.id)}
                      />
                    ))
                  }
                </>
              )
            }

            return (
              <Form.Item name="role" label={getRoleLabel(client)} dependencies={['client']} rules={[{ required: true, message: `${apiKeyLabels.singular} ${getRoleLabel(client)} is required` }]}>
                <Select placeholder={`Select ${getRoleLabel(client)}`} showSearch allowClear disabled={!client || !labeledItems} loading={client && !roles && !labeledItems} options={labeledItems} />
              </Form.Item>
            )
          }}
        </Form.Item>
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <Button type="button" variant="light" size="sm" className="border" onClick={onCancel}>Close</Button>
        <Button type="submit" variant="success" size="sm">
          <FontAwesomeIcon icon={solid('save')} />
          <span className="ms-2">Save</span>
        </Button>
      </Modal.Footer>
    </Form>
  );
}

type SelectableItemProps = (
  { type: 'model'; item: BdaModel } | { type: 'application'; item: BdaApplication } |
  { type: 'rag', item: Rag } | { type: string, item: any }
) & {
  selected?: boolean;
  onSelect?: VoidFunction;
}

const SelectableItem = memo(({ item, type, selected, onSelect }: SelectableItemProps) => {
  const handleSelect = useCallback(() => onSelect?.(), [onSelect]);

  return (
    <div className={classNames('selectable-item', { selected })} key={item.id}>
      <IconButton className="select-icon px-3" onClick={handleSelect} type="button">
        <FontAwesomeIcon size="lg" className="action" icon={selected ? solid('check-circle') : regular('circle')} />
      </IconButton>
      <Col className="py-1 overflow-hidden">
        <div className="text-truncate">
          <Link to={`${type === 'application' ? '/bda-applications' : `${type === 'rag' ? '/llm' : ''}/${type}s`}/${item.id}`} target="_blank">{item.name}</Link>
        </div>
        <Flex align="center">
          <Tooltip title="Access Level">
            <Badge>{item.accessLevel}</Badge>
          </Tooltip>
          {type === 'application' && (
            <Tooltip title="Version">
              <Badge bg="light" className="ms-1 border text-dark">
                <FontAwesomeIcon icon={solid('tag')} />
                <span className="ms-2">{item.version}</span>
              </Badge>
            </Tooltip>
          )}
          <Badge bg="light" className="ms-1 border text-dark">
            <FontAwesomeIcon icon={solid('user')} size="sm" />
            <span className="ms-1">{item.owner?.name}</span>
          </Badge>
          {item.createDateTime && (
            <Tooltip title="Created">
              <small className="ms-1 text-muted">{formatDate(item.createDateTime!)}</small>
            </Tooltip>
          )}
        </Flex>
      </Col>
    </div>
  )
});
